// Shared Manga V3 inline style objects.
// Used across the app via: style={isMangaV3 ? mv3.card : undefined}
// This is NOT global CSS — each component opts in via isMangaV3 ternary.
// Tokens aligned with MV3 constants in MangaV3Layout.tsx.
import type { CSSProperties } from 'react';

// ─── Tokens ───
const BORDER = '#0a0a0a';
const ACCENT = '#E8192C';
const INK = '#1a1410';
const MUTED = '#8b7e6a';
const WHITE = '#ffffff';
const FONT_DISPLAY = "'Bangers', 'Impact', cursive";

// ─── Reusable style objects ───
export const mv3 = {

  // Cards & containers (prominent elements)
  card: {
    background: WHITE,
    border: `3px solid ${BORDER}`,
    borderRadius: 2,
    boxShadow: '3px 3px 0 rgba(0,0,0,0.2)',
  } as CSSProperties,

  // Cards without shadow (rows, list items)
  cardFlat: {
    background: WHITE,
    border: `2px solid ${BORDER}`,
    borderRadius: 2,
  } as CSSProperties,

  // Inputs, selects, textareas
  input: {
    borderRadius: 2,
    border: `2px solid ${BORDER}`,
    boxShadow: 'none',
    background: WHITE,
  } as CSSProperties,

  // Tab bar container
  tabBar: {
    background: WHITE,
    border: `3px solid ${BORDER}`,
    borderRadius: 2,
    boxShadow: '3px 3px 0 rgba(0,0,0,0.2)',
    padding: '6px 8px',
  } as CSSProperties,

  // Active tab
  tabActive: {
    background: ACCENT,
    color: WHITE,
    fontFamily: FONT_DISPLAY,
    letterSpacing: '1px',
    textTransform: 'uppercase' as const,
    border: `2px solid ${BORDER}`,
    borderRadius: 2,
    fontWeight: 400,
  } as CSSProperties,

  // Inactive tab
  tabInactive: {
    color: INK,
    fontFamily: FONT_DISPLAY,
    letterSpacing: '1px',
    textTransform: 'uppercase' as const,
    background: 'transparent',
    border: 'none',
    fontWeight: 400,
  } as CSSProperties,

  // Filter pills / chips (inactive)
  pill: {
    border: `2px solid ${BORDER}`,
    borderRadius: 2,
    background: WHITE,
    color: INK,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as CSSProperties,

  // Filter pills / chips (active)
  pillActive: {
    border: `2px solid ${BORDER}`,
    borderRadius: 2,
    background: ACCENT,
    color: WHITE,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as CSSProperties,

  // Pill shape only — preserves original bg/color from Tailwind classes.
  // Use for colored labels (Collected, Visited, Not Visited, etc.)
  pillOutline: {
    borderRadius: 2,
    border: `2px solid ${BORDER}`,
  } as CSSProperties,

  // Section headings (dark)
  heading: {
    fontFamily: FONT_DISPLAY,
    letterSpacing: '1px',
    color: INK,
  } as CSSProperties,

  // Section headings (red accent)
  headingRed: {
    fontFamily: FONT_DISPLAY,
    letterSpacing: '1.5px',
    textTransform: 'uppercase' as const,
    color: ACCENT,
  } as CSSProperties,

  // Halftone overlay for images (parent must be position:relative)
  halftone: {
    position: 'absolute' as const,
    inset: 0,
    backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)',
    backgroundSize: '4px 4px',
    pointerEvents: 'none' as const,
    zIndex: 1,
  } as CSSProperties,

  // Progress bar track
  progress: {
    background: '#e5e5e5',
    borderRadius: 2,
    border: `2px solid ${BORDER}`,
    overflow: 'hidden' as const,
  } as CSSProperties,

  // Progress bar fill
  progressFill: {
    borderRadius: 0,
  } as CSSProperties,

  // Icon backgrounds (squared instead of circular)
  iconSquare: {
    borderRadius: 4,
    border: `2px solid ${BORDER}`,
  } as CSSProperties,

  // Accordion / collapsible sections
  accordion: {
    background: WHITE,
    border: `2px solid ${BORDER}`,
    borderRadius: 2,
  } as CSSProperties,

  // Modal containers
  modal: {
    borderRadius: 2,
    border: `3px solid ${BORDER}`,
    boxShadow: '4px 4px 0 rgba(0,0,0,0.25)',
  } as CSSProperties,

  // Text utility styles
  ink: { color: INK } as CSSProperties,
  accent: { color: ACCENT } as CSSProperties,
  muted: { color: MUTED } as CSSProperties,

} as const;
